/**
 * LoginPage
 *
 * Microsoft-only sign-in against the SAME backend as the Staff Audit
 * Scheduler (POST /auth/microsoft). This module has no separate user base
 * -- signing in here is signing in to the same account used for
 * Scheduling/Timesheet. The email/password form was removed on request;
 * AuthContext.login() (email/password) still exists and works if it's
 * ever needed again -- only this page's UI dropped it.
 */
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth.js';
import { loginRequest } from '../config/msalConfig.js';

const MicrosoftLogo = () => (
  <svg viewBox="0 0 21 21" className="h-4 w-4" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

export const LoginPage = () => {
  const { isAuthenticated, loginWithMicrosoft, error } = useAuth();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMicrosoftBusy, setIsMicrosoftBusy] = useState(false);

  const redirectTo = location.state?.from?.pathname ?? '/';

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleMicrosoftLogin = async () => {
    setIsMicrosoftBusy(true);
    try {
      const result = await instance.loginPopup(loginRequest);
      // Backend validates this via Microsoft Graph, which only accepts an
      // ACCESS token -- result.idToken looks valid but is rejected with
      // "Invalid or expired Microsoft token." Sent as `access_token` in the
      // request body (confirmed against the live backend).
      const ok = await loginWithMicrosoft(result.accessToken);
      if (ok) navigate(redirectTo, { replace: true });
    } catch {
      // Popup closed/cancelled by the user, or MSAL config issue -- the
      // "Microsoft sign-in failed" state from loginWithMicrosoft (if it got
      // that far) already covers the error message; nothing else to do here.
    } finally {
      setIsMicrosoftBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-base px-4">
      <div className="w-full max-w-sm rounded-lg border border-edge bg-surface p-6 shadow-sm">
        <div className="mb-6 text-center">
          <p className="text-sm font-semibold tracking-wide text-primary">WIP System</p>
          <p className="mt-1 text-xs text-muted">Scheduling vs. Timesheet Comparison</p>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-400">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="button"
          onClick={handleMicrosoftLogin}
          disabled={isMicrosoftBusy}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-edge px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-surface-hover disabled:opacity-60"
        >
          {isMicrosoftBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MicrosoftLogo />
          )}
          Sign in with Microsoft
        </button>

        <p className="mt-4 text-center text-xs text-muted">
          Uses the same sign-in as Staff Audit Scheduling &amp; Timesheet.
        </p>
      </div>
    </div>
  );
};
